// screen: Dashboard
import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useLingui, Trans } from '@lingui/react/macro';
import { ApiError, qk, type StatsRealtime, type StatsSeries, type StatsTraffic, type JobSummary } from '@viewstream/api-client';
import { EmptyState, ErrorState, KpiTile, Skeleton, cn } from '@viewstream/ui';
import { Player } from '@viewstream/player-react';
import { Radio, Clapperboard, BarChart3 } from 'lucide-react';
import { api } from '~/app/api';
import { ChannelStateBadge, FeedBadges, useChannels, useNow } from '~/app/live';
import { p95Of5m } from '~/app/traffic';
import { liveCdn, livePosterUrl } from '~/app/thumbs';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { fmtNumber, fmtBitrate } from '@viewstream/i18n';
import { appTitle } from '~/app/brand';
import { TodayByHourChart, TodayVsYesterdayCard } from '~/app/analytics';
import { FailedJobsDrawer, useJobFailures } from '~/app/FailedJobs';
import { FirstRunChecklist } from '~/app/Plan';

export const Route = createFileRoute('/_layout/t/$slug')({ component: Dashboard });

/** A stats block is "not available yet" on 404 / feature_disabled (control-plane M6 not deployed) — foundation spec scenario. */
function notYet(err: unknown): boolean {
  return err instanceof ApiError && (err.isNotFound || err.isFeatureDisabled);
}

function Dashboard() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`Dashboard`); }, [t]);
  const cdn = tenant.mode === 'cdn';
  return (
    <div>
      <h1 className="mb-4 text-2xl font-semibold">{t`Dashboard`}</h1>
      {cdn ? null : <FirstRunChecklist tenant={tenant.slug} />}
      <RealtimeStrip />
      {cdn ? <CdnTraffic24h /> : null}
      {cdn ? null : (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <ChannelTiles />
          <Pipeline />
        </div>
      )}
      <TodayVsYesterday />
    </div>
  );
}

/** Realtime strip: fed by the `stats.realtime` SSE event written into the cache; this query polls every 10 s ONLY
 *  while SSE is down (the SSE hook sets the data, keeping it fresh; `refetchInterval` yields to fresh data). */
function RealtimeStrip() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const q = useQuery({
    queryKey: qk.statsRealtime(tenant.slug),
    queryFn: async () => (await api.GET('/v1/stats/realtime')).data as StatsRealtime,
    refetchInterval: (query) => (query.state.dataUpdatedAt > Date.now() - 12_000 ? false : 10_000),
    retry: false,
  });
  if (q.isPending) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <KpiTile key={i} label={<Skeleton className="h-3 w-16" />} value="" loading />)}</div>;
  if (q.isError) {
    if (notYet(q.error)) return <NotYet what={t`Realtime statistics`} />;
    return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load statistics`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  }
  const d = q.data;
  const cdn = tenant.mode === 'cdn';
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cdn ? (
        <>
          <KpiTile label={t`Traffic now`} value={fmtBitrate((d.gbps?.total ?? 0) * 1e9, locale)} />
          <KpiTile label={t`Concurrent viewers`} value={fmtNumber(d.concurrent?.total ?? 0, locale)} />
          <KpiTile label={t`Plays (5 min)`} value={fmtNumber(d.plays_last_5m ?? 0, locale)} />
          <KpiTile label={t`Errors / min`} value={fmtNumber(d.fatal_errors_per_min ?? 0, locale)} />
        </>
      ) : (
        <>
          <KpiTile label={<span title={t`Players that report viewing (embeds, sites, player pages). Studio previews are not counted.`}>{t`Concurrent viewers`}</span>} value={fmtNumber(d.concurrent?.total ?? 0, locale)} hint={`${t`Live`} ${fmtNumber(d.concurrent?.live ?? 0, locale)} · VOD ${fmtNumber(d.concurrent?.vod ?? 0, locale)} · ${t`Studio previews not counted`}`} />
          <KpiTile label={t`Plays (last hour)`} value={fmtNumber(d.plays_last_hour ?? d.plays_last_5m ?? 0, locale)} />
          <KpiTile label={t`Top channel`} value={d.top_channels?.[0]?.title ?? d.top_channels?.[0]?.slug ?? '—'} hint={d.top_channels?.[0] ? fmtNumber(d.top_channels[0].concurrent, locale) : undefined} />
          <KpiTile label={t`Traffic now`} value={fmtBitrate((d.gbps?.total ?? 0) * 1e9, locale)} />
        </>
      )}
    </div>
  );
}

function ChannelTiles() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const q = useChannels(tenant.slug);
  const now = useNow(10_000); // the recorder writes poster.jpg every 10 s
  const { locale } = useLocale();
  // viewers now per channel: the stats.realtime snapshot (same cache entry as the strip, kept fresh by SSE)
  const rt = useQuery({
    queryKey: qk.statsRealtime(tenant.slug),
    queryFn: async () => (await api.GET('/v1/stats/realtime')).data as StatsRealtime,
    refetchInterval: (query) => (query.state.dataUpdatedAt > Date.now() - 12_000 ? false : 10_000),
    retry: false,
  });
  const viewersOf = (slug?: string): string => {
    const top = rt.data?.top_channels;
    if (!top || !slug) return '—';
    const hit = top.find((c) => c.slug === slug);
    if (hit) return fmtNumber(hit.concurrent, locale);
    // the snapshot lists the top 5 channels: a channel missing from a shorter list has nobody watching
    return top.length < 5 ? fmtNumber(0, locale) : '—';
  };
  return (
    <section aria-labelledby="channels-h">
      <h2 id="channels-h" className="mb-3 text-lg font-semibold">{t`Live channels`}</h2>
      {q.isPending ? <Skeleton className="h-40 w-full" /> : q.isError ? (
        notYet(q.error) ? <NotYet what={t`Live channels`} compact /> : <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load channels`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : (q.data?.length ?? 0) === 0 ? (
        <EmptyState icon={<Radio className="size-8" aria-hidden />} title={t`No channels yet`} description={t`Create a channel to see its feed status and viewers here.`}
          actions={<Link className="text-sm text-primary underline-offset-4 hover:underline" to={`/t/${tenant.slug}/live` as never}><Trans>Go to Live</Trans></Link>} />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {q.data!.map((c) => (
            <li key={c.id}>
              {/* the whole card opens the channel's Monitor (Dmitry 2026-09-28); the tile preview still plays on hover */}
              <Link to={'/t/$slug/live/$channelId' as never} params={{ slug: tenant.slug, channelId: c.id } as never} search={{ tab: 'monitor' } as never}
                aria-label={t`Open monitor for ${c.title ?? c.slug}`} data-testid="channel-tile"
                className="block cursor-pointer rounded-lg border border-border bg-surface p-3 shadow-card transition-colors hover:border-primary/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                <Player mode="tile" src={c.state === 'live' ? c.playback?.live : undefined} poster={livePoster(c, tenant.cdn_hostname, tenant.slug, now)} label={t`Preview of ${c.title ?? c.slug}`} className="mb-2" />
                <div className="flex items-center justify-between">
                  <span className="font-medium">{c.title ?? c.slug}</span>
                  <ChannelStateBadge state={c.state} />
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-fg-muted">
                  <FeedBadges ch={c} />
                  <span className="ms-auto">{t`viewers`}: <bdi className="num tabular-nums">{viewersOf(c.slug)}</bdi></span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Pipeline() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const [drawer, setDrawer] = React.useState(false);
  const statuses: JobSummary['status'][] = ['running', 'queued'];
  const q = useQuery({
    queryKey: qk.jobs(tenant.slug, 'dashboard'),
    queryFn: async () => {
      const out: Record<string, number> = {};
      for (const s of statuses) out[s] = ((await api.GET('/v1/jobs', { params: { query: { status: s, limit: 200 } } })).data?.items ?? []).length;
      return out;
    },
    retry: false,
    refetchInterval: 30_000,
  });
  // change dashboard-failed-jobs: failed = the last 24 h, "still open" = not superseded, not fixed, not permanent
  const f = useJobFailures();
  const total = f.data?.total ?? 0;
  const openN = f.data?.open ?? 0;
  return (
    <section aria-labelledby="pipeline-h">
      <h2 id="pipeline-h" className="mb-3 text-lg font-semibold">{t`Pipeline`}</h2>
      {q.isError && notYet(q.error) ? <NotYet what={t`Pipeline`} compact /> : q.isError ? (
        <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load jobs`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <KpiTile label={t`Encoding`} value={fmtNumber(q.data?.['running'] ?? 0, locale)} loading={q.isPending} />
          <KpiTile label={t`Queued`} value={fmtNumber(q.data?.['queued'] ?? 0, locale)} loading={q.isPending} />
          <button type="button" onClick={() => setDrawer(true)} data-testid="failed-tile" aria-haspopup="dialog"
            className="rounded-lg text-start focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label={t`Failed in the last 24 hours: ${fmtNumber(total, locale)}, still open: ${fmtNumber(openN, locale)}. Show details`}>
            <KpiTile label={t`Failed (24 h)`} value={fmtNumber(total, locale)} loading={f.isPending}
              hint={f.data ? (openN > 0 ? t`${fmtNumber(openN, locale)} still open · details` : t`none open · details`) : undefined}
              className={cn('h-full cursor-pointer transition-colors hover:border-primary/60', openN > 0 ? 'border-danger/40' : undefined)} />
          </button>
        </div>
      )}
      <p className="mt-2 text-xs text-fg-muted"><Clapperboard className="me-1 inline size-3.5" aria-hidden /><Trans>Assets move registered → probing → encoding → packaging → ready.</Trans></p>
      <FailedJobsDrawer open={drawer} onOpenChange={setDrawer} />
    </section>
  );
}

/** Live poster (recorder snapshot, ≤ ~20 s old) with a 10 s cache-buster so the tile follows the channel. */
function livePoster(c: { slug?: string; state?: string; playback?: { live?: string; live_host?: string } | null }, host: string | null | undefined, tenant: string, now: number): string | undefined {
  if (!c.slug || (c.state !== 'live' && c.state !== 'degraded')) return undefined;
  const u = livePosterUrl(liveCdn(c, host), tenant, c.slug);
  return u ? `${u}?t=${Math.floor(now / 10_000)}` : undefined;
}

/** CDN-only tenants: hit ratio, origin egress and the 95th percentile over the last 24 h (same stats API as Analytics). */
function CdnTraffic24h() {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const { locale } = useLocale();
  const to = Math.ceil(Date.now() / 60_000) * 60_000;
  const w = { from: new Date(to - 86_400_000).toISOString(), to: new Date(to).toISOString() };
  const tot = useQuery({
    queryKey: qk.stats(tenant.slug, 'traffic', { dash: '24h' }),
    queryFn: async () => (await api.GET('/v1/stats/traffic', { params: { query: { ...w } } })).data as StatsTraffic,
    refetchInterval: 120_000, retry: false,
  });
  const series = useQuery({
    queryKey: qk.stats(tenant.slug, 'timeseries', { metric: 'gbps', dash: '24h' }),
    queryFn: async () => (await api.GET('/v1/stats/timeseries', { params: { query: { metric: 'gbps', interval: '1m', ...w } } })).data as StatsSeries,
    refetchInterval: 120_000, retry: false,
  });
  const r = tot.data?.rows?.[0];
  const pct = (v: number) => fmtNumber(v, locale, { style: 'percent', maximumFractionDigits: 1 });
  const gb = (v: number) => (v >= 1e12 ? `${fmtNumber(v / 1e12, locale, { maximumFractionDigits: 2 })} TB` : `${fmtNumber(v / 1e9, locale, { maximumFractionDigits: 2 })} GB`);
  if (tot.isError && notYet(tot.error)) return null;
  return (
    <section className="mt-6" aria-labelledby="cdn24-h">
      <h2 id="cdn24-h" className="mb-3 text-lg font-semibold">{t`Last 24 hours`}</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile label={t`Delivered`} value={gb(r?.bytes ?? 0)} loading={tot.isPending} />
        <KpiTile label={t`Cache hit ratio`} value={pct(r?.hit_ratio_bytes ?? 0)} hint={`${t`Requests`} ${pct(r?.hit_ratio_requests ?? 0)}`} loading={tot.isPending} />
        <KpiTile label={t`Origin egress`} value={gb(r?.origin_bytes ?? 0)} loading={tot.isPending} />
        <KpiTile label={t`95th percentile`} value={`${fmtNumber(p95Of5m(series.data?.points ?? []), locale, { maximumFractionDigits: 3 })} Gbps`} hint={t`5-minute averages`} loading={series.isPending} />
      </div>
    </section>
  );
}

/** Today (since midnight Israel time) vs the same span yesterday — /v1/stats/overview (change p5-analytics). */
function TodayVsYesterday() {
  const { t } = useLingui();
  return (
    <section className="mt-6" aria-labelledby="today-h">
      <h2 id="today-h" className="mb-3 text-lg font-semibold">{t`Today vs yesterday`}</h2>
      <TodayVsYesterdayCard />
      <div className="mt-4"><TodayByHourChart /></div>
    </section>
  );
}

function NotYet({ what, compact }: { what: string; compact?: boolean }) {
  const { t } = useLingui();
  return (
    <EmptyState icon={compact ? undefined : <BarChart3 className="size-8" aria-hidden />} title={t`${what}: not available yet`}
      description={t`This data arrives when the statistics service (Insight) is deployed. Nothing else on this page depends on it.`} className={compact ? 'p-6' : undefined} />
  );
}
